import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { GIFT_ICON, dollars } from "@/lib/giftIcons";
import { Wallet as WalletIcon, TrendingUp, Gift, Landmark, BadgeCheck, Target, X, Lock } from "lucide-react";
import { toast } from "sonner";

const TOPUPS = [
  { key: "wallet_1000", amount: 1000 },
  { key: "wallet_2500", amount: 2500 },
  { key: "wallet_5000", amount: 5000 },
  { key: "wallet_10000", amount: 10000 },
];

export default function Wallet() {
  const [params, setParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [received, setReceived] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [connect, setConnect] = useState(null);
  const [goal, setGoal] = useState(null);
  const [goalTitle, setGoalTitle] = useState("");
  const [goalAmount, setGoalAmount] = useState("");
  const [priv, setPriv] = useState(null);
  const [rateInput, setRateInput] = useState("");
  const [savingPriv, setSavingPriv] = useState(false);
  const [loadingKey, setLoadingKey] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);

  const load = () => {
    api.get("/wallet").then(({ data }) => setWallet(data));
    api.get("/gifts/received").then(({ data }) => setReceived(data));
    api.get("/payouts").then(({ data }) => setPayouts(data));
    api.get("/connect/status").then(({ data }) => setConnect(data));
    api.get("/auth/me").then(({ data }) => {
      setPriv({ rate: data.private_rate || 0, available: !!data.private_available });
      api.get(`/gifts/goal/${data.id}`).then(({ data: g }) => setGoal(g.goal));
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const c = params.get("connect");
    if (c === "return") {
      toast.success("Stripe onboarding complete — verifying your payout account…");
      setParams({}, { replace: true });
    } else if (c === "refresh") {
      toast.info("Onboarding was interrupted — you can restart it below.");
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const topup = async (key) => {
    setLoadingKey(key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout");
      setLoadingKey("");
    }
  };

  const startOnboarding = async () => {
    setOnboarding(true);
    try {
      const { data } = await api.post("/connect/onboard");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Stripe onboarding");
      setOnboarding(false);
    }
  };

  const requestPayout = async () => {
    setRequesting(true);
    try {
      const { data } = await api.post("/payouts/request", {});
      toast.success(`Payout of ${dollars(data.amount)} sent to your bank`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payout failed");
    } finally {
      setRequesting(false);
    }
  };

  const saveGoal = async () => {
    const cents = Math.round(parseFloat(goalAmount) * 100);
    if (!goalTitle.trim() || !cents || cents < 500) {
      toast.error("Give your goal a title and an amount of at least $5");
      return;
    }
    setSavingGoal(true);
    try {
      const { data } = await api.post("/gifts/goal", { title: goalTitle.trim(), amount: cents });
      setGoal(data);
      setGoalTitle("");
      setGoalAmount("");
      toast.success("Gift goal is live — it shows during your video dates");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set goal");
    } finally {
      setSavingGoal(false);
    }
  };

  const clearGoal = async () => {
    await api.delete("/gifts/goal");
    setGoal(null);
    toast.success("Goal cleared");
  };

  const savePrivate = async (available) => {
    const payload = {};
    if (rateInput) {
      const cents = Math.round(parseFloat(rateInput) * 100);
      if (!cents || cents < 299) {
        toast.error("Set a per-minute rate of at least $2.99 first");
        return;
      }
      payload.rate = cents;
    }
    if (available !== undefined) payload.available = available;
    if (Object.keys(payload).length === 0) {
      toast.error("Enter a per-minute rate first");
      return;
    }
    setSavingPriv(true);
    try {
      const { data } = await api.post("/private/settings", payload);
      setPriv({ rate: data.private_rate, available: data.private_available });
      setRateInput("");
      if (available === true) toast.success("You're now available for private sessions");
      else if (available === false) toast.success("Private availability stopped");
      else toast.success("Rate saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update settings");
    } finally {
      setSavingPriv(false);
    }
  };

  if (!wallet) return <div className="pt-32 text-center text-text-muted">Loading…</div>;

  const payoutsReady = connect?.payouts_enabled;
  const goalPct = goal ? Math.min(100, Math.round((goal.progress / goal.amount) * 100)) : 0;

  return (
    <div className="pt-24 pb-24 max-w-4xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Your wallet</p>
        <h1 className="font-display text-6xl font-light mb-10">Balance &amp; earnings</h1>
      </motion.div>

      <div className="grid sm:grid-cols-2 gap-6 mb-6">
        <div className="rounded-3xl border border-white/10 bg-surface p-8">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-text-muted"><WalletIcon size={15} /> Spending wallet</p>
          <p data-testid="wallet-balance" className="font-display text-5xl font-light mt-4">{dollars(wallet.wallet_balance)}</p>
          <p className="text-xs text-text-muted mt-2">Use this to send gifts during chats &amp; live dates.</p>
          <div className="grid grid-cols-2 gap-3 mt-6">
            {TOPUPS.map((t) => (
              <button key={t.key} data-testid={`topup-${t.amount}`} disabled={loadingKey === t.key} onClick={() => topup(t.key)}
                className="py-3 rounded-full bg-wine hover:bg-wine-hover transition-colors text-sm disabled:opacity-50">
                {loadingKey === t.key ? "…" : `Add ${dollars(t.amount)}`}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-gold/30 bg-surface p-8">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><TrendingUp size={15} /> Creator earnings</p>
            {payoutsReady && (
              <span data-testid="payouts-active-badge" className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-400 border border-emerald-500/40 px-2.5 py-1 rounded-full">
                <BadgeCheck size={12} /> Payouts active{connect?.simulated ? " · test" : ""}
              </span>
            )}
          </div>
          <p data-testid="earnings-balance" className="font-display text-5xl font-light mt-4 text-gold">{dollars(wallet.earnings_balance)}</p>
          <p className="text-xs text-text-muted mt-2">Gifts you receive, minus a {Math.round(wallet.platform_fee_pct * 100)}% platform fee. Min payout $20.</p>
          {(wallet.earned_from_gifts > 0 || wallet.earned_from_private > 0) && (
            <div data-testid="earnings-breakdown" className="mt-5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-text-muted mb-2">Lifetime earnings</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/10 px-4 py-3">
                  <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-text-muted"><Gift size={11} className="text-gold" /> From gifts</p>
                  <p data-testid="earned-gifts" className="text-gold text-base mt-1">{dollars(wallet.earned_from_gifts)}</p>
                </div>
                <div className="rounded-xl border border-white/10 px-4 py-3">
                  <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-text-muted"><Lock size={11} className="text-gold" /> Private minutes</p>
                  <p data-testid="earned-private" className="text-gold text-base mt-1">{dollars(wallet.earned_from_private)}</p>
                </div>
              </div>
            </div>
          )}

          {!payoutsReady ? (
            <>
              <button data-testid="connect-onboard" disabled={onboarding} onClick={startOnboarding}
                className="mt-6 w-full py-3.5 rounded-full border border-gold/50 text-gold hover:bg-gold/10 transition-colors text-sm uppercase tracking-[0.2em] disabled:opacity-40 flex items-center justify-center gap-2">
                <Landmark size={15} /> {onboarding ? "Opening Stripe…" : connect?.connected ? "Finish bank setup" : "Set up payouts"}
              </button>
              <p className="text-[11px] text-text-muted mt-3">
                {connect?.connected && connect?.details_submitted
                  ? "Stripe is verifying your details — payouts unlock shortly."
                  : "Connect your bank securely via Stripe to cash out earnings."}
              </p>
            </>
          ) : (
            <button data-testid="request-payout" disabled={requesting || wallet.earnings_balance < 2000} onClick={requestPayout}
              className="mt-6 w-full py-3.5 rounded-full bg-gold text-ink hover:bg-gold/90 transition-colors text-sm uppercase tracking-[0.2em] disabled:opacity-40">
              {requesting ? "Sending…" : "Cash out to bank"}
            </button>
          )}
        </div>
      </div>

      <div data-testid="goal-card" className="rounded-3xl border border-white/10 bg-surface p-8 mb-6">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-text-muted"><Target size={15} className="text-gold" /> Live gift goal</p>
        {goal ? (
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <p className="font-display text-2xl">{goal.title}</p>
              <button data-testid="clear-goal" onClick={clearGoal} className="p-2 border border-white/10 rounded-full text-text-muted hover:border-white/30 transition-colors"><X size={14} /></button>
            </div>
            <div className="h-2 mt-4 rounded-full bg-white/10 overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${goal.reached ? "bg-emerald-400" : "bg-gold"}`} style={{ width: `${goalPct}%` }} />
            </div>
            <p data-testid="goal-status" className="text-sm mt-3 text-text-muted">
              {goal.reached
                ? <span className="text-emerald-400">Goal reached — congratulations.</span>
                : <>{dollars(goal.progress)} of {dollars(goal.amount)} gifted ({goalPct}%)</>}
            </p>
            <p className="text-[11px] text-text-muted mt-2">Your admirers see this bar fill up live during video dates.</p>
          </div>
        ) : (
          <div className="mt-4">
            <p className="text-sm text-text-muted mb-4">Set a goal your admirers can gift toward — it appears as a live progress bar during your video dates.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input data-testid="goal-title-input" value={goalTitle} onChange={(e) => setGoalTitle(e.target.value)} maxLength={60}
                placeholder="e.g. New lingerie set" className="flex-1 bg-black/30 border border-white/10 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-gold/50" />
              <input data-testid="goal-amount-input" value={goalAmount} onChange={(e) => setGoalAmount(e.target.value)} type="number" min="5" step="1"
                placeholder="Amount ($)" className="sm:w-36 bg-black/30 border border-white/10 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-gold/50" />
              <button data-testid="set-goal" disabled={savingGoal} onClick={saveGoal}
                className="px-8 py-3 rounded-full bg-wine hover:bg-wine-hover transition-colors text-sm uppercase tracking-[0.15em] disabled:opacity-50">
                {savingGoal ? "…" : "Set goal"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div data-testid="private-card" className="rounded-3xl border border-white/10 bg-surface p-8 mb-6">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-text-muted"><Lock size={15} className="text-gold" /> Private sessions</p>
          {priv?.available && (
            <span data-testid="private-available-badge" className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-400 border border-emerald-500/40 px-2.5 py-1 rounded-full">
              <span className="w-2 h-2 rounded-full bg-emerald-400" /> Available now
            </span>
          )}
        </div>
        <p className="text-sm text-text-muted mt-4">
          Offer pay-per-minute private video dates. You keep 80% of every minute. Minimum rate $2.99/min.
          {priv?.rate >= 299 && <span data-testid="private-current-rate" className="text-gold"> Current rate: {dollars(priv.rate)}/min</span>}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mt-5">
          <input data-testid="private-rate-input" value={rateInput} onChange={(e) => setRateInput(e.target.value)} type="number" min="2.99" step="0.01"
            placeholder="Rate ($ / minute)" className="sm:w-48 bg-black/30 border border-white/10 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-wine/60" />
          <button data-testid="private-save-rate" disabled={savingPriv} onClick={() => savePrivate(undefined)}
            className="px-6 py-3 rounded-full border border-white/15 hover:border-wine transition-colors text-sm uppercase tracking-[0.15em] disabled:opacity-50">
            Save rate
          </button>
          <button data-testid="private-toggle" disabled={savingPriv} onClick={() => savePrivate(!priv?.available)}
            className={`px-8 py-3 rounded-full text-sm uppercase tracking-[0.15em] transition-colors disabled:opacity-50 ${priv?.available ? "border border-red-500/50 text-red-400 hover:bg-red-500/10" : "bg-wine hover:bg-wine-hover"}`}>
            {priv?.available ? "Stop availability" : "Go available"}
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/8 bg-surface p-6">
          <h3 className="flex items-center gap-2 font-display text-2xl mb-4"><Gift size={18} className="text-gold" /> Gifts received</h3>
          {received.length === 0 ? <p className="text-sm text-text-muted">No gifts yet.</p> : (
            <div className="space-y-3">
              {received.slice(0, 8).map((g, i) => {
                const Icon = GIFT_ICON[g.gift_id] || GIFT_ICON.heart;
                return (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2"><Icon size={16} className="text-gold" /> {g.gift_name} <span className="text-text-muted">from {g.from_name}</span></span>
                    <span className="text-gold">+{dollars(g.creator_credit)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-white/8 bg-surface p-6">
          <h3 className="font-display text-2xl mb-4">Payout history</h3>
          {payouts.length === 0 ? <p className="text-sm text-text-muted">No payouts yet.</p> : (
            <div className="space-y-3">
              {payouts.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm">
                  <span>{dollars(p.amount)}</span>
                  <span className={`text-xs uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border ${p.status === "paid" ? "text-emerald-400 border-emerald-500/40" : "text-amber-300 border-amber-500/40"}`}>{p.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
